'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { AlertTriangle, X, Bell, ExternalLink } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface RealtimeAlert {
  id: string;
  type: 'critical_issue' | 'vendor_change' | 'dd_finding';
  title: string;
  description: string;
  href: string;
  timestamp: string;
}

const TYPE_CONFIG = {
  critical_issue: {
    label: 'Critical Issue',
    icon: AlertTriangle,
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-400',
    badge: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  },
  vendor_change: {
    label: 'Vendor Change',
    icon: Bell,
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  },
  dd_finding: {
    label: 'DD Finding',
    icon: Bell,
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
  },
};

export default function DashboardRealtimeAlerts() {
  const [alerts, setAlerts] = useState<RealtimeAlert[]>([]);
  const counterRef = useRef(0);

  useEffect(() => {
    const supabase = createClient();

    const addAlert = (alert: RealtimeAlert) => {
      setAlerts((prev) => {
        if (prev.some((a) => a.id === alert.id)) return prev;
        // Keep max 5 alerts visible
        return [alert, ...prev].slice(0, 5);
      });
      // Auto-dismiss after 12 seconds
      setTimeout(() => {
        setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      }, 12000);
    };

    // Critical issues
    const issuesChannel = supabase
      .channel('dash_rt_issues')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'issues' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const severity = row.severity as string;
          if (severity !== 'Critical' && severity !== 'High') return;
          const id = `dash-issue-${counterRef.current++}`;
          addAlert({
            id,
            type: 'critical_issue',
            title: `New ${severity} Issue: ${row.title || 'Untitled'}`,
            description: `${row.vendor_name || 'A vendor'} — ${row.description || 'Review required.'}`,
            href: '/issues',
            timestamp: new Date().toISOString(),
          });
        }
      )
      .subscribe();

    // Vendor risk escalations
    const vendorsChannel = supabase
      .channel('dash_rt_vendors')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'vendors' },
        (payload) => {
          const oldRow = payload.old as Record<string, unknown>;
          const newRow = payload.new as Record<string, unknown>;
          const oldTier = oldRow.risk_tier as string;
          const newTier = newRow.risk_tier as string;
          const escalated =
            (newTier === 'Critical' && oldTier !== 'Critical') ||
            (newTier === 'High' && oldTier !== 'High' && oldTier !== 'Critical');
          if (!escalated) return;
          const id = `dash-vendor-${counterRef.current++}`;
          addAlert({
            id,
            type: 'vendor_change',
            title: `Risk Escalation: ${newRow.name || 'Vendor'}`,
            description: `Risk tier changed from ${oldTier} to ${newTier}. Immediate review required.`,
            href: '/vendor-management',
            timestamp: new Date().toISOString(),
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'vendors' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const id = `dash-vendor-new-${counterRef.current++}`;
          addAlert({
            id,
            type: 'vendor_change',
            title: `New Vendor Added: ${row.name || 'Unknown'}`,
            description: `Added to ${row.lifecycle_stage || 'Intake'} stage. Initial review pending.`,
            href: '/vendor-management',
            timestamp: new Date().toISOString(),
          });
        }
      )
      .subscribe();

    // DD findings
    const ddChannel = supabase
      .channel('dash_rt_dd')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dd_reviews' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const id = `dash-dd-${counterRef.current++}`;
          addAlert({
            id,
            type: 'dd_finding',
            title: `New DD Review: ${row.vendor_name || 'Vendor'}`,
            description: `${row.domain || 'Security'} domain review initiated. Risk tier: ${row.risk_tier || 'TBD'}.`,
            href: '/due-diligence-workspace',
            timestamp: new Date().toISOString(),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(issuesChannel);
      supabase.removeChannel(vendorsChannel);
      supabase.removeChannel(ddChannel);
    };
  }, []);

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2">
      {alerts.map((alert) => {
        const cfg = TYPE_CONFIG[alert.type];
        const AlertIcon = cfg.icon;
        return (
          <div
            key={alert.id}
            className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${cfg.bg} ${cfg.border} animate-in slide-in-from-top-2 duration-300`}
          >
            <AlertIcon size={16} className={`flex-shrink-0 mt-0.5 ${cfg.text}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>
                  {cfg.label}
                </span>
                <span className={`text-sm font-semibold ${cfg.text}`}>{alert.title}</span>
              </div>
              <p className="text-xs text-[hsl(var(--text-secondary))] mt-0.5 leading-snug">
                {alert.description}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Link
                href={alert.href}
                className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border ${cfg.border} ${cfg.text} hover:bg-white/50 dark:hover:bg-white/5 transition-colors`}
              >
                View
                <ExternalLink size={10} />
              </Link>
              <button
                onClick={() => setAlerts((prev) => prev.filter((a) => a.id !== alert.id))}
                className="p-1 rounded text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
